/* eslint-disable eqeqeq */
import { useEffect, useState } from 'react'
import { sendGet } from '../../hooks/use-fetch'
import { Column, ColumnSort, ColumnType, type RowData } from '../../Components/Table/DataTable/DataTable.model'
import { Box, Chip, Typography } from '@mui/material'
import DataTable from '../../Components/Table/DataTable'
import { type PaginationProps, usePagination, type SearchCriteria } from '../../hooks/use-pagination'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { CareMangementSearchOptions, type CareMangementSearchCriteria, CaseloadFilterOption } from './CareManagementSearchOptions'
import { generatePath, useNavigate } from 'react-router'
import * as AppRoutes from '../Routes'
import { FormatDateValue } from '../../core/Utilities'

const childNameColumn = new Column('childName', 'Child Name')
const ageColumn = new Column('age', 'Age')
const startDateColumn = new Column('startDate', 'Start Date', ColumnType.CUSTOM)
startDateColumn.customCellGenerator = row => {
  return <div>
    <div>{FormatDateValue(row.startDate)}</div>
  </div>
}

const coordinatorColumn = new Column('coordinatorName', 'Assigned To', ColumnType.CUSTOM)
coordinatorColumn.customCellGenerator = row => {
  return <div>
    {row.careManagementUsers.map((h: { userID: number, userName: string }) => {
      return <div key={'coordinator' + h.userName}>{h.userName}</div>
    })}
  </div>
}
coordinatorColumn.sortable = false

const addressColumn = new Column('address', 'Resident Address', ColumnType.CUSTOM)
addressColumn.customCellGenerator = row => {
  return <div>
    <div>{row.address} </div>
    <div>{row.city} {row.state} {row.zipCode}</div>
  </div>
}
addressColumn.sortable = false

const careGiversColumn = new Column('caregiverName', 'Caregivers', ColumnType.CUSTOM)
careGiversColumn.customCellGenerator = row => {
  return <div>
    {row.childSearchCaregivers.map((h: { firstName: string, lastName: string, contactPhone: string }) => {
      return <div key={'caregiver' + h.firstName + h.lastName}>{h.firstName} {h.lastName}</div>
    })}
  </div>
}
careGiversColumn.sortable = false

const contactColumn = new Column('caregiverContact', 'Contact Phone', ColumnType.CUSTOM)
contactColumn.customCellGenerator = row => {
  const content = row.childSearchCaregivers.map((h: { firstName: string, lastName: string, contactPhone: string }) => {
    return <div key={h.firstName + h.lastName + 'phone'}>{h.contactPhone}</div>
  })
  return <div>
    {content}
  </div>
}
contactColumn.sortable = false

const servicesColumn = new Column('services', 'Services', ColumnType.CUSTOM)
const reportedFPLColumn = new Column('reportedFPL', 'Reported FPL', ColumnType.CUSTOM)
reportedFPLColumn.customCellGenerator = row => {
  return <>{row.reportedFPL}%</>
}
servicesColumn.customCellGenerator = row => {
  // pick one of 3 colors by doing a modulo on the id
  const listItems: JSX.Element[] = []
  const listNames: string[] = []
  row.childServices.map((s: { serviceID: number, serviceName: string }) => {
    const colorItem = s.serviceID % 3
    let indicatorClass = 'cyan-'
    if (colorItem === 1) {
      indicatorClass = 'purple-'
    } else if (colorItem === 2) {
      indicatorClass = 'red-'
    }
    if (!listNames.includes(s.serviceName)) {
      listNames.push(s.serviceName)
      listItems.push(<Chip key={s.serviceID?.toString() + 'indicator'} icon={<FiberManualRecordIcon className={indicatorClass + 'icon'} />} label={s.serviceName} size='small' className={indicatorClass + 'indicator'} />)
    }
    return s.serviceName
  })
  servicesColumn.sortable = false

  return <div className='stacked-items'>
    {listItems}
  </div>
}

const columns = [childNameColumn, startDateColumn, ageColumn, coordinatorColumn, addressColumn, careGiversColumn, contactColumn, reportedFPLColumn, servicesColumn]

export function CareManagementList (): JSX.Element {
  const [loading, setLoading] = useState(true)
  const [listItems, setListItems] = useState<RowData[]>([])
  const [searchData, setSearchData] = useState<CareMangementSearchCriteria>({
    caseloadStatus: CaseloadFilterOption.MyCaseload,
    childName: '',
    showUnassigned: false
  })

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response, error } = await sendGet('/CareManagement/CareManagementList', {})
      if (error[0] === '' && response !== null) {
        setListItems(response)
      }
      setLoading(false)
      handleSearch(searchData)
    }
    void loadData()
  }, [])

  const handleSearch = (newdata: CareMangementSearchCriteria): void => {
    setSearchData(newdata)
    const fieldArray: SearchCriteria[] = []
    // need to use == instead of === to get enum comparison to work, sadly
    if (newdata.caseloadStatus == CaseloadFilterOption.MyCaseload) {
      fieldArray.push({
        field: 'assignedToCurrentUser',
        value: 'true'
      })
    } else if (newdata.caseloadStatus == CaseloadFilterOption.OrganizationCaseload) {
      fieldArray.push({
        field: 'assignedToCurrentProvider',
        value: 'true'
      })
      if (newdata.showUnassigned) {
        fieldArray.push({
          field: 'hasUserAssignment',
          value: 'false'
        })
      }
    }

    if (newdata.childName !== '') {
      fieldArray.push({
        field: 'childName',
        value: newdata.childName
      })
    }

    pagination.setSearchFields(fieldArray)
  }

  const paginationProps: PaginationProps<RowData> = {
    rows: listItems,
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)
  const nav = useNavigate()

  const rowClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): void => {
    const editChild = `${generatePath(AppRoutes.CARENETWORK_MANAGEMENT_DETAILS, { id: row.childID })}?from=cm`
    nav(editChild)
  }

  return <>
    <Typography variant="h3">Care Management</Typography>
    <CareMangementSearchOptions current={searchData} onChange={handleSearch} />

    <Box sx={{ width: '100%', padding: '30px 20px', borderRadius: '16px', background: '#fff' }}>
      <DataTable
        page={pagination.page}
        name='careManagementTable'
        hasCheckbox={false}
        columns={columns}
        rows={pagination.internalRows}
        totalRecords={pagination.recordCount}
        loading={loading}
        onSortChange={(col, sorts) => {
          pagination.onSortChange(col, sorts[0].order)
        }}
        onPageChange={pagination.handleChangePage}
        onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        onRowClick={rowClick}
        initialColumnSorts={[new ColumnSort('childName')]}
      />
    </Box>

  </>
}
